import React, {
  forwardRef,
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  useCallback,
  useState,
  ChangeEvent,
  useEffect,
} from 'react';
import { useFocusRing } from '@react-aria/focus';
import { AriaFocusProps } from '../../types/common';
import { css } from '@emotion/react';
import {
  input_wrapper_base_css,
  input_base_css,
  input_default_style,
  input_disabled_style,
  input_error_style,
  input_status_color_css,
  input_status_icon_css,
  input_focus_style,
  getInputSizeStyle,
  getInputFontStyle,
  getInputStatusIconSize,
} from './styles';
import { InputAffixProps } from './InputAffix';
import { InputAlignment, InputSize, InputStatus } from './types';
import { IconCheck, IconWarning, IconProps } from '../icons/generated';
import { BaseText } from '../text';
import { semantic_colors } from '../../foundation';
import { CSSValueWithLength, size } from '../../styles';
import classNames from 'classnames';

export interface InputBaseType {
  /** @default 'medium' */
  size?: InputSize;
  /** @default 'left' */
  alignment?: InputAlignment;
  /** @default 'info' */
  status?: InputStatus;
  /** 좌측 영역에 렌더링 될 요소 */
  leftIcon?: ReactElement<IconProps> | ReactNode;
  /** 우측 영역에 렌더링 될 요소 */
  rightIcon?: ReactElement<IconProps> | ReactNode;
  /** Input prefix 요소 */
  leftAddon?: InputAffixProps['prefix'];
  /** Input suffix 요소 */
  rightAddon?: InputAffixProps['suffix'];
  width?: CSSValueWithLength;
  flex?: React.CSSProperties['flex'];
  /**
   * 입력된 value count 노출 여부
   * @default false
   */
  hasCounter?: boolean;
  /**
   * 아이콘 노출 여부
   * @default true
   */
  hasStatusIcon?: boolean;
  /**
   * HTML에 전달되는 size 값
   */
  htmlSize?: number;
}

export type InputBaseProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'width' | 'size'> &
  InputBaseType &
  AriaFocusProps;

export const controlledInputValue = (value?: string | number | readonly string[]) => {
  if (typeof value === 'undefined' || value === null) {
    return '';
  }
  return String(value);
};

export const InputBase = forwardRef<HTMLInputElement, InputBaseProps>(
  (
    {
      value,
      size: sizeProp = 'medium',
      status = 'info',
      disabled,
      alignment = 'left',
      leftIcon,
      rightIcon,
      leftAddon,
      rightAddon,
      style,
      width,
      flex,
      className,
      hasCounter = false,
      hasStatusIcon = true,
      maxLength,
      unstableFocusVisible,
      onChange,
      htmlSize,
      ...props
    },
    ref,
  ) => {
    const { isFocusVisible, focusProps } = useFocusRing({ within: true, isTextInput: true });
    const [charLength, setCharLength] = useState<number>(0);
    const status_style = {
      ...input_default_style,
      ...((unstableFocusVisible ?? isFocusVisible) && input_focus_style),
      ...(disabled && input_disabled_style),
      ...(status && status === 'error' && input_error_style),
    };
    const input_color = disabled
      ? value
        ? semantic_colors.content.secondary
        : semantic_colors.content.disabled
      : semantic_colors.content.primary;
    const statusIconSize = getInputStatusIconSize(sizeProp);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const currentCharLength = controlledInputValue(e.target.value).length;
        const isUnderMaxLength = maxLength ? currentCharLength <= maxLength : true;

        if (isUnderMaxLength) {
          hasCounter && setCharLength(currentCharLength);
          onChange?.(e);
        }
      },
      [onChange, maxLength, hasCounter],
    );

    useEffect(() => {
      hasCounter && setCharLength(controlledInputValue(value).length);
    }, [value]);

    const renderToInputStatus = (position: InputAlignment | InputAlignment[]) => {
      if (!hasStatusIcon) return null;
      return (
        <>
          {position.includes(alignment) && status !== 'info' && (
            <span css={input_status_icon_css}>
              {status === 'success' && <IconCheck color={input_status_color_css.success} size={statusIconSize} />}
              {status === 'error' && <IconWarning size={statusIconSize} color={input_status_color_css.error} />}
            </span>
          )}
        </>
      );
    };

    const renderToCounter = () => {
      if (!hasCounter) return null;

      return (
        <BaseText
          typography='Body_12_Regular'
          alignment='trailing'
          color={status === 'error' ? semantic_colors.state.negative : semantic_colors.content.secondary}
        >
          {maxLength ? `${charLength}/${maxLength}` : `${charLength}`}
        </BaseText>
      );
    };

    return (
      <div
        className={classNames(['pds-input-wrapper', className])}
        css={[
          input_wrapper_base_css,
          getInputSizeStyle(sizeProp),
          status_style,
          size({ width }),
          flex !== undefined &&
            css`
              flex: ${flex};
            `,
          { ...style },
          css`
            ${leftAddon && 'border-top-left-radius: 0; border-bottom-left-radius: 0'};
            ${rightAddon && 'border-top-right-radius: 0; border-bottom-right-radius: 0'}
          `,
        ]}
        {...focusProps}
      >
        {alignment === 'right' && renderToCounter()}
        {leftIcon}
        {renderToInputStatus('right')}
        <input
          className='pds-input'
          css={[
            input_base_css,
            getInputFontStyle(sizeProp),
            css`
              ${alignment && `text-align: ${alignment}`};
              color: ${input_color};
              ${disabled && ` -webkit-text-fill-color: ${input_color}`};
            `,
            style?.textAlign && { textAlign: style.textAlign },
          ]}
          value={value}
          disabled={disabled}
          maxLength={maxLength}
          onChange={handleChange}
          size={htmlSize}
          {...props}
          ref={ref}
        />
        {renderToInputStatus(['left', 'center'])}
        {rightIcon}
        {(alignment === 'left' || alignment === 'center') && renderToCounter()}
      </div>
    );
  },
);
